import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import Image from "next/image";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { BasicInfo } from "@/types/dto/common/basic-info";

interface BrandingBarProps {
  bannerData?: BasicInfo;
}

const BrandingBar = ({ bannerData }: BrandingBarProps) => {
    const [imageError, setImageError] = useState(false);

    // Use API data
    const displayTagline = bannerData?.headerText;
    
    // Parse benefits from API copyText
    const parseBenefitsFromHtml = (htmlString?: string): string[] => {
        if (!htmlString) return [];
        
        // Extract text content from <p> tags
        const regex = /<p>(.*?)<\/p>/g;
        const benefits: string[] = [];
        const matches = htmlString.match(regex);
        
        if (matches) {
            matches.forEach((match) => {
                const textContent = match.replace(/<\/?p>/g, "");
                benefits.push(textContent);
            });
        }
        
        return benefits;
    };
    
    const displayBenefits = parseBenefitsFromHtml(bannerData?.copyText);
    
    // Check if essential content is available
    const hasValidContent = displayTagline && displayBenefits.length > 0 && 
                           displayBenefits.every(benefit => benefit.length > 0);

    // Hide module if text content fails
    if (!hasValidContent) {
        return null;
    }

    const backgroundStyle = imageError 
        ? { backgroundColor: "background.brand" }
        : {
            background: "url('https://images.ctfassets.net/69jewsgul74b/2C2zTjH8GZVpuv4hc4pLWo/b4b28512dd9707c16674979bc60ee3ec/footeremailpattern.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat"
          };

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "80px",
                display: "flex",
                alignItems: "center",
                ...backgroundStyle,
                color: "common.white",
                padding: {
                    xs: "1.2rem 0",
                    md: "1rem 0"
                }
            }}
            className="fluid-box"
            onError={() => setImageError(true)}
            data-track="visibility"
            data-parent-container-type="pdp"
            data-module-name="Branding Banner"
            data-ranking-algorithm="manual"
            data-module-ranking-position="6"
            data-testid="pdp-branding-bar-section-root"
            component="section"
        >
            {/* Hidden image for error detection */}
            <Image
                src="/images/branding-banner.jpg"
                alt=""
                width={1}
                height={1}
                style={{ display: "none" }}
                onError={() => setImageError(true)}
                onLoad={() => setImageError(false)}
                priority={false}
            />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "center", md: "center" },
                    justifyContent: "center",
                    maxWidth: {xs:"300px", sm: "100%"},
                    gap: {xs:"12px", md: "40px" },
                    margin: "0 auto"
                }}
            >
                {/* Left side - Main tagline */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "2rem", sm: "1.8rem", md: "2rem" }
                    }}
                    data-testid="branding-bar-tagline"
                >
                    {displayTagline}<sup>®</sup>
                </Typography>

                {/* Right side - Benefits list */}
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    // spacing={{ xs: "8px", md: 4 }}
                    sx={{
                        alignItems: { xs: "flex-start", md: "center" },
                        alignSelf: {xs:"flex-start", md:"auto"}
                    }}
                >
                    {/* On mobile: single column, On desktop: two columns */}
                    <Box sx={{ display: { xs: "block", sm: "none" } }} data-testid="branding-bar-benefits-mobile">
                        <Stack spacing="6px">
                            {displayBenefits.map((benefit, idx) => (
                                <Box
                                    key={`mobile-${benefit.replace(/[^a-zA-Z0-9]/g, "")}`}
                                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                                    data-testid={`branding-bar-benefit-mobile-${idx}`}
                                >
                                    <LocationOnOutlinedIcon sx={{ fontSize: 20, color: "secondary.main" }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: "1.4rem",
                                            fontWeight: 500,
                                            letterSpacing: "0.4px"
                                        }}
                                        data-testid={`branding-bar-benefit-text-mobile-${idx}`}
                                    >
                                        {benefit}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    {/* Desktop layout: two columns */}
                    <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 4 }} data-testid="branding-bar-benefits-desktop">
                        <Stack spacing="10px">
                            {displayBenefits.slice(0, 2).map((benefit, idx) => (
                                <Box
                                    key={`left-${benefit.replace(/[^a-zA-Z0-9]/g, "")}`}
                                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                                    data-testid={`branding-bar-benefit-desktop-left-${idx}`}
                                >
                                    <LocationOnOutlinedIcon sx={{ fontSize: 20, color: "secondary.main" }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: "1.4rem",
                                            fontWeight: 500,
                                            letterSpacing: "0.4px"
                                        }}
                                        data-testid={`branding-bar-benefit-text-desktop-left-${idx}`}
                                    >
                                        {benefit}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Stack spacing="10px">
                            {displayBenefits.slice(2, 4).map((benefit, idx) => (
                                <Box
                                    key={`right-${benefit.replace(/[^a-zA-Z0-9]/g, "")}`}
                                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                                    data-testid={`branding-bar-benefit-desktop-right-${idx}`}
                                >
                                    <LocationOnOutlinedIcon sx={{ fontSize: 20, color: "secondary.main" }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: "1.4rem",
                                            fontWeight: 500,
                                            letterSpacing: "0.4px"
                                        }}
                                        data-testid={`branding-bar-benefit-text-desktop-right-${idx}`}
                                    >
                                        {benefit}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

export default BrandingBar;
